import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CircleNumber } from '@/components/ui/circle-number';
import { Badge } from '@/components/ui/badge';
import { Pencil, Trash2, GripVertical } from 'lucide-react';
import { Filter } from '@/types/filter';

interface FilterCardProps {
  filter: Filter;
  onEdit: () => void;
  onDelete: () => void;
  dragHandleProps?: any;
}

export function FilterCard({
  filter,
  onEdit,
  onDelete,
  dragHandleProps,
}: FilterCardProps) {
  const conditionCount = filter.conditions.length;
  const groupCount = filter.conditionGroups.length;
  const totalConditions =
    conditionCount +
    filter.conditionGroups.reduce((sum, group) => sum + group.conditions.length, 0);

  const getConditionSummary = () => {
    if (totalConditions === 0) {
      return 'No conditions defined';
    }

    const parts = [];
    if (conditionCount > 0) {
      parts.push(`${conditionCount} condition${conditionCount !== 1 ? 's' : ''}`);
    }
    if (groupCount > 0) {
      parts.push(`${groupCount} group${groupCount !== 1 ? 's' : ''}`);
    }

    return parts.join(', ');
  };

  return (
    <Card className="p-4 bg-white hover:bg-slate-50 dark:hover:bg-gray-900 transition-colors cursor-pointer group relative rounded-lg">
      <div className="flex items-start lg:items-center gap-3">
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 self-center"
        >
          <GripVertical className="h-5 w-5" />
        </div>

        {/* Filter Order Number */}
        <CircleNumber number={filter.order} className="shrink-0 self-center" />

        {/* Filter Details */}
        <div className="flex-1 min-w-0 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-1 lg:gap-4" onClick={onEdit}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-sm text-blue-950 dark:text-white break-words">{filter.name}</h3>
              {filter.optional && (
                <Badge variant="secondary" className="text-xs py-0 px-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-300 dark:border-amber-700">
                  Optional
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 lg:hidden">
              {getConditionSummary()}
            </p>
          </div>
          <p className="text-sm text-slate-500 hidden lg:block shrink-0 text-right whitespace-nowrap">
            {getConditionSummary()}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="hidden lg:flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute right-4 top-1/2 -translate-y-1/2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
